import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
  OnDestroy,
  inject,
  DestroyRef,
  untracked,
} from '@angular/core';
import { ActivatedRoute, QueryParamsHandling, Router } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { PermissionDirective } from '../../directives/permissions.directive';
import { TabItem, TabMenuStyle } from './tab-menu.model';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { TabMenuLinkWrapperComponent } from './components/tab-menu-link-wrapper/tab-menu-link-wrapper.component';
import { TabMenuButtonLinkWrapperComponent } from './components/tab-menu-button-link-wrapper/tab-menu-button-link-wrapper.component';
import { Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';
import { TabMenuMoreButtonComponent } from './components/tab-menu-more-button/tab-menu-more-button.component';

@Component({
  selector: 'twc-tab-menu',
  templateUrl: './tab-menu.component.html',
  styleUrls: ['./tab-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatRippleModule,
    MatButtonModule,
    PermissionDirective,
    CdkDrag,
    CdkDropList,
    TabMenuLinkWrapperComponent,
    TabMenuButtonLinkWrapperComponent,
    TabMenuMoreButtonComponent,
  ],
})
export class TabMenuComponent<T = unknown> implements OnDestroy {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _menuPadding = 20;
  private readonly _resize$ = new Subject<boolean>();

  private _resizeObserver?: ResizeObserver;

  menuItems = input.required<TabItem[]>();
  /** @deprecated use queryParamHandling in every MenuItem */
  queryParamHandling = input<QueryParamsHandling | null>(null);
  replaceURL = input<boolean>(true);
  menuStyle = input<TabMenuStyle>(TabMenuStyle.DEFAULT);
  menuItemArgument = input<T>();
  dropped = output<TabItem[]>();

  internalMenuItems = signal<TabItem[]>([]);
  isDraggable = computed(() =>
    this.internalMenuItems().some((item) => item.draggable)
  );
  overflowItems = signal<TabItem[]>([]);
  tabMenuList = viewChild<ElementRef<HTMLDivElement>>('tabMenuList');

  get parentElement(): HTMLElement {
    return this.tabMenuList()?.nativeElement?.parentElement?.parentElement;
  }

  get menuElement(): HTMLElement {
    return this.tabMenuList()?.nativeElement;
  }

  constructor() {
    this._setupResizeListener();
    this._setupResizeObserver();
    this._setupMenuItemsObserver();
  }

  ngOnDestroy() {
    this._resizeObserver?.disconnect();
  }

  onItemClick(item: TabItem) {
    this.setActiveItem(item);
    item.clickFn?.(item);
  }

  setActiveItem(item: TabItem) {
    this.internalMenuItems.update((items) =>
      items.map((value) => ({
        ...value,
        active: value.title === item.title,
      }))
    );
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.internalMenuItems(),
      event.previousIndex,
      event.currentIndex
    );
    this._recalculateOrder();
    this.dropped.emit(this.internalMenuItems());
  }

  sortPredicate(index: number): boolean {
    return this.internalMenuItems()[index]?.draggable ?? false;
  }

  overflowItemSelected(item: TabItem) {
    this.setActiveItem(item);

    if (item.url) {
      this._navigateToUrl(item);
    } else if (item.clickFn) {
      item.clickFn(item);
    }

    /**
     * Based on requirements, when an overflow item is selected, it should be returned back to the main menu.
     */
    this._returnOverflowItemToMenu(item);
  }

  private _setupResizeListener() {
    this._resize$
      .pipe(debounceTime(200), takeUntilDestroyed(this._destroyRef))
      .subscribe((fits) => this._adjustMenuItems(fits));
  }

  private _setupResizeObserver() {
    effect(() => {
      if (this.tabMenuList()) {
        this._resizeObserver?.disconnect();
        this._observeMenuResize();
      }
    });
  }

  private _setupMenuItemsObserver() {
    effect(() => {
      const menuItems = this.menuItems();

      untracked(() => {
        this.overflowItems.set([]);
        this.internalMenuItems.set(menuItems);

        if (this.menuItems().length > 0) {
          this._recalculateOrder();

          setTimeout(() => {
            this._adjustMenuItems(this._checkIfMenuFits());
          });
        }
      });
    });
  }

  private _observeMenuResize() {
    this._resizeObserver = new ResizeObserver(() => {
      this._resize$.next(this._checkIfMenuFits());
    });

    this._resizeObserver.observe(this.parentElement);
  }

  private _adjustMenuItems(fits: boolean) {
    if (fits) {
      this._expandMenu();
    } else {
      this._shrinkMenu();
    }
  }

  private _shrinkMenu() {
    if (this.internalMenuItems().length === 0) {
      return;
    }

    const lastInactiveItem = this._getLastInactiveItem();
    if (!lastInactiveItem) {
      return;
    }

    this._moveItemFromMenuToOverflow(lastInactiveItem);

    setTimeout(() => {
      if (!this._checkIfMenuFits() && this.internalMenuItems().length > 0) {
        this._shrinkMenu();
      }
    }, 0);
  }

  private _expandMenu() {
    if (this.overflowItems().length === 0) {
      return;
    }

    const firstOverflowItem = this.overflowItems()[0];
    if (!firstOverflowItem) {
      return;
    }

    this._moveItemFromOverflowToMenu(firstOverflowItem);

    setTimeout(() => {
      if (!this._checkIfMenuFits()) {
        this._moveItemFromMenuToOverflow(firstOverflowItem);
      } else if (this.overflowItems().length > 0 && this._hasExtraSpace()) {
        this._expandMenu();
      }
    }, 0);
  }

  private _returnOverflowItemToMenu(item: TabItem) {
    this._moveItemFromOverflowToMenu(item);
    this.setActiveItem(item);
    setTimeout(() => {
      this._adjustMenuItems(this._checkIfMenuFits());
    }, 0);
  }

  private _moveItemFromOverflowToMenu(item: TabItem) {
    this.overflowItems.update((items) => items.filter((i) => i !== item));
    this.internalMenuItems.update((items) =>
      [...items, item].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
  }

  private _moveItemFromMenuToOverflow(item: TabItem) {
    this.internalMenuItems.update((items) => items.filter((i) => i !== item));
    this.overflowItems.update((items) => [item, ...items]);
  }

  private _recalculateOrder() {
    this.internalMenuItems.update((items) =>
      items.map((item, i) => ({ ...item, order: i }))
    );
  }

  private _getLastInactiveItem(): TabItem | undefined {
    const items = this.internalMenuItems();
    for (let i = items.length - 1; i >= 0; i--) {
      if (!items[i].active) {
        return items[i];
      }
    }
    return undefined;
  }

  private _checkIfMenuFits(): boolean {
    const parent = this.parentElement;
    const menuList = this.menuElement;

    if (!menuList || !parent) {
      return true;
    }

    return menuList.scrollWidth + this._menuPadding <= parent.offsetWidth;
  }

  private _hasExtraSpace(): boolean {
    const parent = this.parentElement;
    const menuList = this.menuElement;

    if (!menuList || !parent) {
      return false;
    }

    const availableSpace =
      parent.offsetWidth - (menuList.scrollWidth + this._menuPadding);
    return availableSpace > this._menuPadding * 2;
  }

  private _navigateToUrl(item: TabItem) {
    this._router.navigate([item.url], {
      queryParamsHandling: item.queryParamHandling,
      replaceUrl: this.replaceURL(),
      queryParams: item.queryParams,
      relativeTo: this._activatedRoute,
    });
  }
}
